import {ZodArray, ZodString, ZodTypeAny} from 'zod';

import {unwrapZodType} from './unwrapZodType';

/**
 * Checks if a value is considered "empty" based on its Zod schema type.
 *
 * - For ZodString: empty if null, undefined, or empty/whitespace-only string
 * - For ZodArray: empty if null, undefined, or empty array
 * - For other types: empty if null or undefined
 *
 * @param value - The value to check
 * @param zodType - The Zod type definition for the value
 * @returns true if the value is considered empty for the given type
 *
 * @example
 * const stringSchema = z.string().optional();
 * isEmptyZodValue('', stringSchema); // true
 * isEmptyZodValue('hello', stringSchema); // false
 *
 * const numberSchema = z.number().optional();
 * isEmptyZodValue(null, numberSchema); // true
 * isEmptyZodValue(0, numberSchema); // false
 *
 * const arraySchema = z.array(z.string()).optional();
 * isEmptyZodValue([], arraySchema); // true
 * isEmptyZodValue(['item'], arraySchema); // false
 */
export function isEmptyZodValue(value: unknown, zodType: ZodTypeAny): boolean {
  const {unwrappedType} = unwrapZodType(zodType);

  if (unwrappedType instanceof ZodString) {
    return !value || (value as string).trim() === '';
  }

  if (unwrappedType instanceof ZodArray) {
    return !value || (value as unknown[]).length === 0;
  }

  return value == null;
}
